// src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fadeIn } from '../../styles/animations';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;
  background-color: ${props => props.theme.colors.light};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

/* Conteneur principal : logo à gauche, 2 lignes à droite */
const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 16px;
    justify-content: space-between;
    align-items: center;
    height: 64px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 12px 24px 12px 0;
  border-right: 1px solid #eeeeee;
  animation: ${fadeIn} 0.8s ease-out forwards;
  flex-shrink: 0;

  img {
    height: 64px;
    width: auto;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    border-right: none;
    padding: 0;
    img { height: 44px; }
  }
`;

/* Colonne droite : 2 lignes empilées */
const RightCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

/* Ligne 1 : coordonnées + bouton */
const ContactRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 28px;
  padding: 10px 0 8px 24px;
  border-bottom: 1px solid #eeeeee;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.textMain};
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: color 0.2s;

  svg {
    color: ${props => props.theme.colors.primary};
    flex-shrink: 0;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const DevisBtn = styled(Link)`
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  text-decoration: none;
  padding: 8px 22px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  border-radius: 3px;
  white-space: nowrap;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: #fff;
  }
`;

/* Ligne 2 : liens nav */
const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding: 0 0 0 24px;
  flex: 1;
`;

const NavItemLink = styled(NavLink)`
  color: ${props => props.theme.colors.textMain};
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-decoration: none;
  padding: 8px 11px 18px;
  position: relative;
  transition: color 0.2s;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;

  &:after {
    content: '';
    position: absolute;
    bottom: 10px;
    left: 11px;
    right: 11px;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover, &.active {
    color: ${props => props.theme.colors.primary};
    &:after { transform: scaleX(1); }
  }
`;

/* Mobile */
const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textMain};
  z-index: 102;
  padding: 4px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }

  svg { width: 26px; height: 26px; }
`;

const MobileNav = styled.nav`
  display: ${props => props.open ? 'flex' : 'none'};
  position: fixed;
  top: 0; right: 0;
  height: 100vh;
  width: 260px;
  background-color: ${props => props.theme.colors.light};
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.12);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  z-index: 101;
  padding: 32px;
`;

const MobileNavLink = styled(NavLink)`
  color: ${props => props.theme.colors.textMain};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 13px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  display: block;
  transition: color 0.2s;

  &:hover, &.active {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileContactInfo = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MobileContactLink = styled.a`
  color: ${props => props.theme.colors.textMain};
  text-decoration: none;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { color: ${props => props.theme.colors.primary}; }
`;

const Overlay = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 100;
`;

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => { closeMobileMenu(); }, [location]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(prev => {
            document.body.style.overflow = !prev ? 'hidden' : 'auto';
            return !prev;
        });
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
        document.body.style.overflow = 'auto';
    };

    const navItems = [
        { path: '/', text: 'ACCUEIL' },
        { path: '/entreprise', text: 'QUI SOMMES NOUS ?' },
        { path: '/techniques', text: 'TECHNIQUES' },
        { path: '/realisations', text: 'RÉALISATIONS' },
        { path: '/blog', text: 'BLOG' },
        { path: '/contact', text: 'CONTACT' }
    ];

    const phoneIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.47 11.47 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z"/>
        </svg>
    );

    const emailIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        </svg>
    );

    return (
        <>
            <HeaderWrapper>
                <HeaderInner>
                    {/* Logo centré verticalement sur toute la hauteur */}
                    <Logo to="/">
                        <img src="/logo_decko_vecto.png" alt="Decko Logo" />
                    </Logo>

                    {/* Colonne droite : 2 lignes */}
                    <RightCol>
                        <ContactRow>
                            <ContactLink href="tel:+33683589905">
                                {phoneIcon} 06 83 58 99 05
                            </ContactLink>
                            <ContactLink href="mailto:contact.deckoterrasses@gmail.com">
                                {emailIcon} contact.deckoterrasses@gmail.com
                            </ContactLink>
                            <DevisBtn to="/contact">Demande de devis</DevisBtn>
                        </ContactRow>
                        <NavRow>
                            {navItems.map((item, index) => (
                                <NavItemLink
                                    key={item.path}
                                    to={item.path}
                                    delay={`${0.1 * (index + 1)}s`}
                                    onClick={closeMobileMenu}
                                >
                                    {item.text}
                                </NavItemLink>
                            ))}
                        </NavRow>
                    </RightCol>

                    {/* Bouton hamburger mobile */}
                    <MobileMenuButton onClick={toggleMobileMenu}>
                        {mobileMenuOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        )}
                    </MobileMenuButton>
                </HeaderInner>
            </HeaderWrapper>

            {/* Menu mobile */}
            <MobileNav open={mobileMenuOpen}>
                {navItems.map(item => (
                    <MobileNavLink key={item.path} to={item.path} onClick={closeMobileMenu}>
                        {item.text}
                    </MobileNavLink>
                ))}
                <MobileContactInfo>
                    <MobileContactLink href="tel:+33683589905">
                        {phoneIcon} 06 83 58 99 05
                    </MobileContactLink>
                    <MobileContactLink href="mailto:contact.deckoterrasses@gmail.com">
                        {emailIcon} contact.deckoterrasses@gmail.com
                    </MobileContactLink>
                </MobileContactInfo>
            </MobileNav>

            <Overlay show={mobileMenuOpen} onClick={closeMobileMenu} />
        </>
    );
};

export default Header;
